import * as React from 'react';
import type { Accessors } from '#types/accessor.ts';
import {
  Bar as StyledBar,
  BarProps as StyledBarProps,
} from '../primitives/Bar';
import { useChart } from '../providers/chart-data-provider';
import { useTooltip } from '../providers/tooltip-provider';
import getScaleBandwidth from '../utils/get-scale-bandwidth';

export type BarProps<Datum, XResult, YResult> = Omit<
  StyledBarProps,
  'x' | 'y' | 'width' | 'height'
> & {
  datum: Datum;
  accessors: Accessors<Datum, XResult, YResult>;
};

// A utility function to retrieve a value for an axis and return the scaled version.
// @ts-ignore
const compose = (scale, accessor) => (d) => scale(accessor(d));

/**
 * Applies apply chart context values to the Bar primitive.
 */
export function Bar<Datum, XResult, YResult>({
  accessors,
  datum,
  highlight,
  ...props
}: BarProps<Datum, XResult, YResult>) {
  const { xScale, yScale, yMax } = useChart();
  const { showTooltip, hideTooltip } = useTooltip();

  const xPoint = compose(xScale, accessors.xAccessor);
  const yPoint = compose(yScale, accessors.yAccessor);

  const barWidth = getScaleBandwidth(xScale);
  const barHeight = yMax - yPoint(datum);

  const barX = xPoint(datum);
  const barY = yMax - barHeight;
  const barCenterPoint = barX + barWidth / 2;

  const handleShowTooltip = () => {
    showTooltip({
      tooltipData: datum,
      tooltipLeft: barCenterPoint,
      tooltipTop: barY,
    });
  };

  const handleHideTooltip = () => {
    hideTooltip();
  };

  return (
    <StyledBar
      x={barX ?? 0}
      y={barY}
      width={barWidth}
      height={barHeight}
      tabIndex={0}
      onFocus={handleShowTooltip}
      onMouseEnter={handleShowTooltip}
      onMouseLeave={handleHideTooltip}
      onBlur={handleHideTooltip}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}
